import axios from 'axios';
import { STRAVA_CONFIG } from '../config';

class StravaActivityService {
    constructor() {
        this.baseUrl = 'https://www.strava.com/api/v3';
        this.activitiesCache = new Map();
        this.lastFetchTime = null;
        this.CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
        this.BATCH_SIZE = 12; // Increased from 6 to 12
        
        // Rate limiting configuration
        this.rateLimit = {
            short: { limit: 30, remaining: 30, reset: 0 },
            long: { limit: 100, remaining: 100, reset: 0 }
        };
        this.onRateLimitUpdate = null;

        // Request queue
        this.requestQueue = [];
        this.isProcessingQueue = false;
        this.lastRequestTime = 0;
        this.MIN_REQUEST_INTERVAL = 5000; // Minimum 5 seconds between requests
    }

    setRateLimitCallback(callback) {
        if (callback === null || typeof callback === 'function') {
            this.onRateLimitUpdate = callback;
        } else {
            console.warn('Invalid rate limit callback provided');
        }
    }

    getAccessToken() {
        return localStorage.getItem('stravaAccessToken');
    }

    handleTokenExpiration() {
        localStorage.removeItem('stravaAccessToken');
        localStorage.removeItem('stravaRefreshToken');
        const authUrl = `https://www.strava.com/oauth/authorize?client_id=${STRAVA_CONFIG.clientId}&response_type=code&redirect_uri=${STRAVA_CONFIG.redirectUri}&scope=read,activity:read_all`;
        window.location.href = authUrl;
    }

    async sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    updateRateLimits(headers) {
        if (!headers) return;

        // Parse rate limit headers
        const limit = headers['x-ratelimit-limit']?.split(',');
        const usage = headers['x-ratelimit-usage']?.split(',');
        const reset = headers['x-ratelimit-reset']?.split(',');

        // Update short-term limit (30 requests per minute)
        this.rateLimit.short = {
            limit: parseInt(limit?.[0] || '30'),
            remaining: parseInt(usage?.[0] || '30'),
            reset: parseInt(reset?.[0] || '0')
        };

        // Update long-term limit (100 requests per 15 minutes)
        this.rateLimit.long = {
            limit: parseInt(limit?.[1] || '100'),
            remaining: parseInt(usage?.[1] || '100'),
            reset: parseInt(reset?.[1] || '0')
        };

        // Update UI if callback exists and is a function
        if (typeof this.onRateLimitUpdate === 'function') {
            try {
                this.onRateLimitUpdate({
                    current: this.rateLimit.long.limit - this.rateLimit.long.remaining,
                    limit: this.rateLimit.long.limit
                });
            } catch (error) {
                console.warn('Error updating rate limit status:', error);
            }
        }
    }

    async waitForRateLimit() {
        const now = Date.now();

        // Ensure minimum time between requests
        const timeSinceLastRequest = now - this.lastRequestTime;
        if (timeSinceLastRequest < this.MIN_REQUEST_INTERVAL) {
            const waitTime = this.MIN_REQUEST_INTERVAL - timeSinceLastRequest;
            console.log(`Waiting ${Math.ceil(waitTime / 1000)} seconds to respect minimum request interval...`);
            await this.sleep(waitTime);
        }

        // Check short-term limit
        if (this.rateLimit.short.remaining <= 0) {
            const waitTime = (this.rateLimit.short.reset * 1000) - now;
            if (waitTime > 0) {
                console.log(`Short-term rate limit reached. Waiting ${Math.ceil(waitTime / 1000)} seconds...`);
                await this.sleep(waitTime);
            }
        }

        // Check long-term limit
        if (this.rateLimit.long.remaining <= 0) {
            const waitTime = (this.rateLimit.long.reset * 1000) - now;
            if (waitTime > 0) {
                console.log(`Long-term rate limit reached. Waiting ${Math.ceil(waitTime / 1000)} seconds...`);
                await this.sleep(waitTime);
            }
        }

        // Add a longer delay between requests to be safe
        await this.sleep(3000);
        this.lastRequestTime = Date.now();
    }

    async queueRequest(fn) {
        return new Promise((resolve, reject) => {
            this.requestQueue.push({ fn, resolve, reject });
            if (!this.isProcessingQueue) {
                this.processQueue();
            }
        });
    }

    async processQueue() {
        if (this.isProcessingQueue || this.requestQueue.length === 0) return;
        
        this.isProcessingQueue = true;
        while (this.requestQueue.length > 0) {
            const { fn, resolve, reject } = this.requestQueue.shift();
            try {
                const result = await this.retryWithBackoff(fn);
                resolve(result);
            } catch (error) {
                reject(error);
            }
        }
        this.isProcessingQueue = false;
    }

    async retryWithBackoff(fn, maxRetries = 3, initialDelay = 10000) {
        let lastError;
        for (let i = 0; i < maxRetries; i++) {
            try {
                await this.waitForRateLimit();
                console.log(`Making request (attempt ${i + 1}/${maxRetries})...`);
                const response = await fn();
                // Update rate limits from response headers
                this.updateRateLimits(response.headers);
                return response;
            } catch (error) {
                lastError = error;
                if (error.response?.status === 429) {
                    // Update rate limits from error response headers
                    this.updateRateLimits(error.response.headers);
                    
                    // Calculate wait time based on rate limit headers
                    const resetTime = error.response.headers['x-ratelimit-reset']?.split(',')[1];
                    if (resetTime) {
                        const waitTime = (parseInt(resetTime) * 1000) - Date.now();
                        if (waitTime > 0) {
                            console.log(`Rate limited. Waiting ${Math.ceil(waitTime / 1000)} seconds based on reset time...`);
                            await this.sleep(waitTime);
                        }
                    } else {
                        // Fallback to exponential backoff if no reset time
                        const delay = initialDelay * Math.pow(2, i);
                        console.log(`Rate limited. No reset time available. Retrying in ${delay}ms...`);
                        await this.sleep(delay);
                    }
                } else if (error.response?.status === 401) {
                    try {
                        console.log('Token expired, attempting refresh...');
                        await this.refreshToken();
                        return await fn();
                    } catch (refreshError) {
                        console.error('Failed to refresh token:', refreshError);
                        this.handleTokenExpiration();
                        throw refreshError;
                    }
                } else {
                    throw error;
                }
            }
        }
        throw lastError;
    }

    async getActivities(page = 1, per_page = 6, startDate = null, endDate = null) {
        const cacheKey = `page_${page}_${startDate}_${endDate}`;
        if (this.activitiesCache.has(cacheKey) && this.lastFetchTime && 
            Date.now() - this.lastFetchTime < this.CACHE_DURATION) {
            console.log('Using cached activities');
            return this.activitiesCache.get(cacheKey);
        }

        try {
            const accessToken = await this.getAccessToken();
            if (!accessToken) {
                console.log('No access token found, redirecting to auth...');
                this.handleTokenExpiration();
                return [];
            }

            console.log('Fetching activities with params:', { page, per_page, startDate, endDate });

            const response = await this.queueRequest(() => 
                axios.get(`${this.baseUrl}/athlete/activities`, {
                    headers: {
                        'Authorization': `Bearer ${accessToken}`
                    },
                    params: {
                        page,
                        per_page,
                        after: startDate ? new Date(startDate).getTime() / 1000 : undefined,
                        before: endDate ? new Date(endDate).getTime() / 1000 : undefined
                    }
                })
            );

            const activities = response.data;
            console.log(`Fetched ${activities.length} activities`);

            // Cache the results
            this.activitiesCache.set(cacheKey, activities);
            this.lastFetchTime = Date.now();

            return activities;
        } catch (error) {
            console.error('Error fetching activities:', error);
            throw error;
        }
    }

    async refreshToken() {
        const refreshToken = localStorage.getItem('stravaRefreshToken');
        if (!refreshToken) {
            this.handleTokenExpiration();
            throw new Error('No refresh token available');
        }

        try {
            await this.waitForRateLimit();
            const response = await this.queueRequest(async () => {
                return axios.post(STRAVA_CONFIG.tokenUrl, {
                    client_id: STRAVA_CONFIG.clientId,
                    client_secret: STRAVA_CONFIG.clientSecret,
                    refresh_token: refreshToken,
                    grant_type: 'refresh_token'
                });
            });

            localStorage.setItem('stravaAccessToken', response.data.access_token);
            localStorage.setItem('stravaRefreshToken', response.data.refresh_token);
            return response.data.access_token;
        } catch (error) {
            console.error('Error refreshing token:', error);
            this.handleTokenExpiration();
            throw error;
        }
    }
}

export const stravaActivityService = new StravaActivityService(); 